import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { RotateCcw, AlertTriangle } from 'lucide-react';

export const CHUNK_RELOAD_STORAGE_KEY = 'plotwise_reload_on_chunk_error';
export const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String((error as { message?: unknown })?.message || '');
  return (
    msg.includes('Importing a module script failed') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module')
  );
}

export function tryReloadOnChunkError(error: unknown): boolean {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) {
    return false;
  }
  try {
    const lastReloadStr = window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
    const lastReload = lastReloadStr ? parseInt(lastReloadStr, 10) : 0;
    const now = Date.now();
    if (!lastReload || Number.isNaN(lastReload) || now - lastReload > CHUNK_RELOAD_COOLDOWN_MS) {
      window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
      window.location.reload();
      return true;
    }
  } catch {
    try {
      window.location.reload();
      return true;
    } catch {}
  }
  return false;
}

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Plotwise ErrorBoundary caught an unhandled error:', error, errorInfo);
    tryReloadOnChunkError(error);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (isChunkLoadError(this.state.error)) {
        return (
          <div
            role="status"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '240px',
              padding: '32px 24px',
              background: '#15251b',
              color: '#f0f5ed',
              borderRadius: '12px',
              border: '1px solid #2e4a36',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              textAlign: 'center',
              margin: '16px auto',
              maxWidth: '560px',
              fontFamily: 'inherit',
            }}
          >
            <RotateCcw
              size={26}
              className="animate-spin"
              style={{ color: '#4ade80', marginBottom: '14px' }}
            />
            <h3
              style={{ margin: '0 0 8px 0', fontSize: '17px', fontWeight: 600, color: '#f0f5ed' }}
            >
              Opdaterer til den nyeste version af Plotwise...
            </h3>
            <p
              style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#a8bcaf', lineHeight: 1.5 }}
            >
              En ny release er frigivet. Siden genindlæses automatisk.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 18px',
                background: '#41634b',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Genindlæs nu
            </button>
          </div>
        );
      }
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '280px',
            padding: '32px 24px',
            background: '#15251b',
            color: '#f0f5ed',
            borderRadius: '12px',
            border: '1px solid #2e4a36',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            textAlign: 'center',
            margin: '16px auto',
            maxWidth: '620px',
            fontFamily: 'inherit',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#f87171',
              marginBottom: '16px',
            }}
          >
            <AlertTriangle size={24} />
          </div>

          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#f0f5ed' }}>
            {this.props.fallbackTitle || 'Der opstod en uventet fejl'}
          </h3>

          <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#a8bcaf', lineHeight: 1.5 }}>
            Dine tegnede marker og revir-data er gemt lokalt. Du kan genindlæse visningen for at
            fortsætte.
          </p>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={this.handleReset}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 18px',
                background: '#41634b',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <RotateCcw size={15} /> Prøv igen
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 18px',
                background: 'transparent',
                color: '#c2d5c8',
                border: '1px solid #3d5845',
                borderRadius: '8px',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Genindlæs hele siden
            </button>
          </div>

          {this.state.error?.message && (
            <details style={{ marginTop: '20px', width: '100%', textAlign: 'left' }}>
              <summary
                style={{
                  fontSize: '11px',
                  color: '#7f9686',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                Tekniske detaljer
              </summary>
              <pre
                style={{
                  marginTop: '8px',
                  padding: '10px',
                  background: '#0d1811',
                  borderRadius: '6px',
                  fontSize: '11px',
                  color: '#f87171',
                  overflowX: 'auto',
                  border: '1px solid #233b2b',
                }}
              >
                {this.state.error.message}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
